import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from './contexts/AppContext.jsx';
import { AuthScreen } from './components/auth.jsx';
import { TopBar } from './components/navigation.jsx';
import { FormModal } from './components/forms.jsx';
import { CycleUtilisationCard, BudgetGroup } from './components/budget.jsx';
import { FIAdvisorSection } from './sections/fi-advisor-section.jsx';
import { BudgetSection } from './sections/budget-section.jsx';
import { Icons } from './components/icons.jsx';
import { getCurrentCyclePeriod, formatCycleId } from './core/dateHelpers.js';
import { SettingsSection } from './sections/settings-section.jsx';
import { DashboardSection } from './sections/dashboard-section.jsx';
import { AssetsSection } from './sections/assets-section.jsx';
import { DRIVE_FOLDER_PATH, DRIVE_FILE_NAME } from './config.jsx';
import { LiabilitiesSection } from './sections/liabilities-section.jsx';
import { InvestmentsSection } from './sections/investments-section.jsx';
import { FixedAssetsSection } from './sections/fixed-assets-section.jsx';
import { MilestonesSection } from './sections/milestones-section.jsx';
import { MoreDrawer } from './components/layout/MoreDrawer.jsx';
import { BottomNav } from './components/layout/BottomNav.jsx';
import { CycleConfirmModal } from './components/modals/CycleConfirmModal.jsx';
import { ScorecardModal } from './components/modals/ScorecardModal.jsx';
import { FabSpendSheet } from './components/budget/FabSpendSheet.jsx';
import { usePWAInstall } from './hooks/usePWAInstall.js';
import { useScrollFAB } from './hooks/useScrollFAB.js';
import { useLocalStoragePersistence } from './hooks/useLocalStoragePersistence.js';
import { useCurrencyHelpers } from './hooks/useCurrencyHelpers.js';
import { useScorecard } from './hooks/useScorecard.js';
import { useCycleManager } from './hooks/useCycleManager.js';
import { useReportGeneration } from './hooks/useReportGeneration.js';

function App() {
    // ── Global Context ────────────────────────────────────────
    const ctx = useAppContext();

    // Destructure what we need from context
    const {
        authUser, authChecked, authRestoring, handleSignedIn, handleSignOut, acquireDriveToken,
        goldInventory, setGoldInventory,
        personalLoans, setPersonalLoans,
        goldLoans, setGoldLoans,
        accounts, setAccounts,
        creditCards, setCreditCards,
        sgBudgetCycle1, setSgBudgetCycle1,
        sgBudgetCycle2, setSgBudgetCycle2,
        lkBudgetDamith, setLkBudgetDamith,
        lkBudgetShan, setLkBudgetShan,
        sgdRate, setSgdRate,
        lkrRate, setLkrRate,
        usdRate, setUsdRate,
        baseCurrency, setBaseCurrency,
        investments, setInvestments,
        invCashSGD, setInvCashSGD,
        invCashUSD, setInvCashUSD,
        moneyMarketFunds, setMoneyMarketFunds,
        fixedAssets, setFixedAssets,
        monthlyIncomes, setMonthlyIncomes,
        financialMilestones, setFinancialMilestones,
        cycleHistory, setCycleHistory,
        cycleOpenedAt, setCycleOpenedAt,
        monthlySnapshots, setMonthlySnapshots,
        handleAddSpend,
        getAllData, applyData, isApplyingRef,
        syncStatus, lastSynced, syncNow, syncStuck, forceReconnect, markDirtyAndSync,
        isOnline, driveBackups, setDriveBackups, driveBackupsLoading, setDriveBackupsLoading, backupStatus, createBackup, restoreBackup,
        DriveAPI,
    } = ctx;

    // ── Auto-save to localStorage & flag dirty ──────────────
    useLocalStoragePersistence({
        goldInventory, personalLoans, goldLoans, accounts, creditCards,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        sgdRate, lkrRate, usdRate, baseCurrency,
        cycleHistory, cycleOpenedAt, monthlySnapshots,
        investments, invCashSGD, invCashUSD, moneyMarketFunds,
        fixedAssets, monthlyIncomes, financialMilestones,
    });

    // ── Auto-sync to Drive: 3s debounce after any state change ──
    useEffect(() => {
        if (!authUser) return;
        if (isApplyingRef.current) return; // skip dirty-flag when state change originates from a Drive pull
        markDirtyAndSync();
    }, [
        goldInventory, personalLoans, goldLoans, accounts, creditCards,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        sgdRate, lkrRate, usdRate, baseCurrency,
        cycleHistory, cycleOpenedAt, monthlySnapshots,
        investments, invCashSGD, invCashUSD, moneyMarketFunds,
        fixedAssets, monthlyIncomes, financialMilestones,
        authUser, markDirtyAndSync,
    ]);

    // ── Currency conversion helpers ──────────────────────────────────
    const { toBase, fmtBase, toMonthlyBase, baseSymbol, baseCurrencyLabel } = useCurrencyHelpers({ lkrRate, usdRate, baseCurrency });

    // ── PWA Detection ───────────────────────────────────────
    const { isRunningAsPWA, showInstallPrompt, handleInstallClick, dismissInstallPrompt } = usePWAInstall();

    // Initial data for reset functionality
    const initialGoldInventory = [];
    const initialPersonalLoans = [];
    const initialGoldLoans = [];
    const initialAccounts = [];

    // ── FAB quick-log sheet state (ephemeral — never persisted) ───
    const [showFAB, setShowFAB] = useState(false);
    const [fabBudget, setFabBudget] = useState(null);
    const [fabCategory, setFabCategory] = useState(null);
    const [fabAmt, setFabAmt] = useState('');
    const [fabNote, setFabNote] = useState('');

    const { fabVisible } = useScrollFAB();

    const [expandedHistoryId, setExpandedHistoryId] = useState(null); // which history card is open
    // ── NG-2: Loan Payment Journal state ────────────────────────────
    // expandedLoanId: which loan's payment history panel is open (ephemeral — not persisted)
    const [expandedLoanId, setExpandedLoanId] = useState(null);
    const [activeSection, setActiveSection] = useState('dashboard');
    const [activeModule, setActiveModule] = useState('gold');
    const [showMoreDrawer, setShowMoreDrawer] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // RPT-03: Milestone Journal modal state (hoisted to avoid Rules-of-Hooks violation)
    const [showMsModal, setShowMsModal] = useState(false);
    const [msForm, setMsForm] = useState({ title: '', type: 'manual', note: '', date: new Date().toISOString().slice(0, 7) });
    const [msEditId, setMsEditId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalType, setModalType] = useState('');

    // handleAddSpend comes from useAppContext()

    // ── Budget Cycle: derive current period and whether a reset is due ──
    const currentCycle = getCurrentCyclePeriod();
    // A reset is needed when the stored cycleId doesn't match the current cycle
    const isNewCycle = cycleOpenedAt && cycleOpenedAt !== currentCycle.cycleId;
    // Keep alias for any existing references
    const currentMonth = currentCycle.cycleId;

    const { showCycleConfirm, setShowCycleConfirm, cycleResetStatus, handleCloseCycle } = useCycleManager({
        cycleOpenedAt, currentCycle,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        setCycleHistory, setCycleOpenedAt,
        setSgBudgetCycle1, setSgBudgetCycle2, setLkBudgetDamith, setLkBudgetShan,
        authUser, isOnline, acquireDriveToken,
        DriveAPI,
    });

    const { showScorecard, setShowScorecard, scorecardData, handleOpenScorecard } = useScorecard({
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        personalLoans, goldLoans,
        currentCycle, monthlySnapshots, setMonthlySnapshots,
        toBase,
    });

    // ── NG-1: FAB quick-log submit ───────────────────────────────
    const handleFABSubmit = useCallback(() => {
        const n = parseFloat(fabAmt);
        if (!n || n <= 0 || !fabBudget || !fabCategory) return;
        const budgetMap = {
            sgCycle1: [setSgBudgetCycle1, sgBudgetCycle1],
            sgCycle2: [setSgBudgetCycle2, sgBudgetCycle2],
            lkA: [setLkBudgetDamith, lkBudgetDamith],
            lkB: [setLkBudgetShan, lkBudgetShan],
        };
        const [setter, getter] = budgetMap[fabBudget];
        handleAddSpend(setter, getter, fabCategory, n, fabNote.trim());
        // Reset sheet
        setFabAmt('');
        setFabNote('');
        setFabCategory(null);
        setShowFAB(false);
    }, [fabAmt, fabBudget, fabCategory, fabNote,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan, handleAddSpend]);

    // FAB budget label + items lookup
    const fabBudgetConfig = {
        sgCycle1: { label: '🇸🇬 Cycle 1', items: sgBudgetCycle1, symbol: '$' },
        sgCycle2: { label: '🇸🇬 Cycle 2', items: sgBudgetCycle2, symbol: '$' },
        lkA: { label: '🇱🇰 Budget A', items: lkBudgetDamith, symbol: '₨' },
        lkB: { label: '🇱🇰 Budget B', items: lkBudgetShan, symbol: '₨' },
    };

    // ── NG-2: Mark personal loan instalment as paid ──────────────────
    const handleMarkLoanPaid = useCallback((loanId, overrideAmount = null) => {
        const today = new Date().toISOString().split('T')[0];
        const period = today.slice(0, 7); // "YYYY-MM"
        setPersonalLoans(prev => prev.map(loan => {
            if (loan.id !== loanId) return loan;
            const payment = overrideAmount ?? loan.monthlyInstallment;
            const type = payment < loan.monthlyInstallment ? 'partial'
                : payment > loan.monthlyInstallment ? 'over' : 'full';
            const entry = { period, date: today, amount: payment, type, note: '' };
            return {
                ...loan,
                principalPaid: (loan.principalPaid || 0) + payment,
                principalRemaining: Math.max(0, loan.principalRemaining - payment),
                monthsLeft: Math.max(0, loan.monthsLeft - 1),
                paymentHistory: [...(loan.paymentHistory || []), entry],
            };
        }));
    }, []);

    // ── NG-2: Mark gold loan interest as paid ────────────────────────
    const handleMarkGoldLoanPaid = useCallback((loanId, paidAmount) => {
        const today = new Date().toISOString().split('T')[0];
        const period = today.slice(0, 7);
        setGoldLoans(prev => prev.map(loan => {
            if (loan.id !== loanId) return loan;
            const entry = { period, date: today, amount: paidAmount, type: 'interest', note: 'Monthly interest' };
            return {
                ...loan,
                unpaidInterest: Math.max(0, (loan.unpaidInterest || 0) - paidAmount),
                paymentHistory: [...(loan.paymentHistory || []), entry],
            };
        }));
    }, []);

    // ── Report Generation (FI, Advisor, Household) ────────────
    const {
        generateFIReport,
        fiReportStatus,
        advisorReportStatus,
        shareWithFI,
        shareAdvisorReport,
        exportAdvisorReportHTML,
        exportAdvisorReportPDF,
        exportHouseholdReportHTML,
        exportHouseholdReportPDF,
    } = useReportGeneration({ authUser, getAllData, acquireDriveToken, DriveAPI });

    // Load backup list when user enters Settings
    const loadDriveBackups = useCallback(async () => {
        if (!authUser || driveBackupsLoading) return;
        setDriveBackupsLoading(true);
        try {
            const token = await acquireDriveToken();
            const files = await DriveAPI.listBackupFiles(token);
            setDriveBackups(files);
        } catch (err) {
            console.error('[iPlanner] Backup list error:', err);
        } finally {
            setDriveBackupsLoading(false);
        }
    }, [authUser, acquireDriveToken]);

    // Load backup list whenever settings section is opened
    useEffect(() => {
        if (activeSection === 'settings' && authUser) {
            loadDriveBackups();
        }
    }, [activeSection, authUser]);

    // NOTE: Sync logic (SYNC-001, SYNC-011) is now handled in AppContext via useSync hook
    // The context manages:
    // - Initial sync on auth
    // - Visibility change sync (tab switch)
    // - Periodic polling
    // See src/contexts/AppContext.jsx and src/hooks/useSync.jsx

    // Calculate gold inventory totals
    const goldTotals = useMemo(() => {
        const totalWeight = goldInventory.reduce((sum, item) => sum + (item.weight || 0), 0);
        const totalPawn = goldInventory.reduce((sum, item) => sum + (item.pawn || 0), 0);
        // Gold pawn values are stored in SGD — convert to base
        const totalBase = goldInventory.reduce((sum, item) => {
            const itemRate = item.quality === '18K' ? sgdRate * 0.75 : sgdRate;
            const sgdValue = (item.weight || 0) * itemRate;
            return sum + toBase(sgdValue, 'SGD');
        }, 0);
        return { totalWeight, totalPawn, totalBase, totalItems: goldInventory.length };
    }, [goldInventory, sgdRate, toBase]);

    // Calculate loan totals
    const loanTotals = useMemo(() => {
        // Convert every loan amount to base currency for dashboard totals
        const personalTotal = personalLoans.reduce((s, l) => s + toBase(l.totalAmount, l.currency || 'SGD'), 0);
        const personalRemaining = personalLoans.reduce((s, l) => s + toBase(l.principalRemaining, l.currency || 'SGD'), 0);
        const personalMonthly = personalLoans.reduce((s, l) => s + toBase(l.monthlyInstallment || 0, l.currency || 'SGD'), 0);
        const goldTotal = goldLoans.reduce((s, l) => s + toBase(l.totalAmount, l.currency || 'LKR'), 0);
        const goldOwed = goldLoans.reduce((s, l) => s + toBase((l.principalRemaining || 0) + (l.unpaidInterest || 0), l.currency || 'LKR'), 0);
        return { personalTotal, personalRemaining, personalMonthly, goldTotal, goldOwed };
    }, [personalLoans, goldLoans, toBase]);

    // Calculate investment totals (all in base currency)
    const investmentTotals = useMemo(() => {
        const securitiesBase = investments.reduce((s, inv) => {
            const mktValueUsd = (inv.units || 0) * (inv.currentPriceUsd || 0);
            const costUsd = (inv.units || 0) * (inv.avgCostUsd || 0);
            return {
                mktValue: s.mktValue + toBase(mktValueUsd, 'USD'),
                cost: s.cost + toBase(costUsd, 'USD'),
            };
        }, { mktValue: 0, cost: 0 });
        const pnl = securitiesBase.mktValue - securitiesBase.cost;
        const pnlPct = securitiesBase.cost > 0 ? (pnl / securitiesBase.cost) * 100 : 0;
        const cashBase = toBase(invCashSGD, 'SGD') + toBase(invCashUSD, 'USD');
        const mmfBase = moneyMarketFunds.reduce((s, f) => s + toBase(f.totalValue || 0, f.currency || 'SGD'), 0);
        const totalBase = securitiesBase.mktValue + cashBase + mmfBase;
        return { securitiesBase: securitiesBase.mktValue, pnl, pnlPct, cashBase, mmfBase, totalBase };
    }, [investments, invCashSGD, invCashUSD, moneyMarketFunds, toBase]);

    // Calculate fixed asset totals (all in base currency)
    const fixedAssetTotals = useMemo(() => {
        // CMV (intrinsic) is used for net worth; FSV is the realistic liquidation figure
        const totalBase = fixedAssets.reduce((s, fa) => s + toBase(fa.cmvLKR || fa.valuationLKR || 0, 'LKR'), 0);
        const fsvBase = fixedAssets.reduce((s, fa) => s + toBase(fa.fsvLKR || 0, 'LKR'), 0);
        const landBase = fixedAssets.filter(f => f.type === 'land').reduce((s, fa) => s + toBase(fa.cmvLKR || fa.valuationLKR || 0, 'LKR'), 0);
        const propertyBase = fixedAssets.filter(f => f.type === 'house').reduce((s, fa) => s + toBase(fa.cmvLKR || fa.valuationLKR || 0, 'LKR'), 0);
        return { totalBase, fsvBase, landBase, propertyBase, count: fixedAssets.length };
    }, [fixedAssets, toBase]);

    // INC-08: Budgeted monthly spend (planned, not actual) — base currency.
    // Used to project potential monthly savings vs income.
    const budgetedMonthlySpend = useMemo(() => {
        const sgPlanned =
            sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0) +
            sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0);
        const lkPlanned =
            lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0) +
            lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0);
        const sgBase = toBase(sgPlanned, 'SGD');
        const lkBase = toBase(lkPlanned, 'LKR');
        return { sgBase, lkBase, totalBase: sgBase + lkBase, sgPlanned, lkPlanned };
    }, [sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan, toBase]);

    // INC-03: Income totals — monthly base-currency aggregates, plus per-category breakdown
    const incomeTotals = useMemo(() => {
        const byCategory = {};
        let totalMonthlyBase = 0;
        monthlyIncomes.forEach(inc => {
            const mb = toMonthlyBase(inc);
            totalMonthlyBase += mb;
            const cat = inc.category || 'other';
            if (!byCategory[cat]) byCategory[cat] = { category: cat, monthlyBase: 0, count: 0 };
            byCategory[cat].monthlyBase += mb;
            byCategory[cat].count += 1;
        });
        return {
            totalMonthlyBase,
            annualBase: totalMonthlyBase * 12,
            byCategory: Object.values(byCategory).sort((a, b) => b.monthlyBase - a.monthlyBase),
            sourceCount: monthlyIncomes.filter(i => i.enabled !== false).length,
        };
    }, [monthlyIncomes, toMonthlyBase]);

    // Filter inventory based on search
    const filteredGoldInventory = useMemo(() => {
        if (!searchQuery) return goldInventory;
        const query = searchQuery.toLowerCase();
        return goldInventory.filter(item =>
            item.name.toLowerCase().includes(query) ||
            item.owner.toLowerCase().includes(query) ||
            item.quality.toLowerCase().includes(query) ||
            item.type.toLowerCase().includes(query)
        );
    }, [goldInventory, searchQuery]);

    const filteredPersonalLoans = useMemo(() => {
        if (!searchQuery) return personalLoans;
        const query = searchQuery.toLowerCase();
        return personalLoans.filter(loan =>
            loan.name.toLowerCase().includes(query) ||
            loan.type.toLowerCase().includes(query)
        );
    }, [personalLoans, searchQuery]);

    const filteredGoldLoans = useMemo(() => {
        if (!searchQuery) return goldLoans;
        const query = searchQuery.toLowerCase();
        return goldLoans.filter(loan =>
            loan.name.toLowerCase().includes(query) ||
            loan.owner.toLowerCase().includes(query) ||
            loan.bank.toLowerCase().includes(query)
        );
    }, [goldLoans, searchQuery]);

    const filteredAccounts = useMemo(() => {
        if (!searchQuery) return accounts;
        const query = searchQuery.toLowerCase();
        return accounts.filter(account =>
            account.accountName.toLowerCase().includes(query) ||
            account.accountNumber.toLowerCase().includes(query) ||
            account.bank.toLowerCase().includes(query) ||
            account.country.toLowerCase().includes(query)
        );
    }, [accounts, searchQuery]);

    // Calculate account totals
    const accountTotals = useMemo(() => {
        // Keep raw multi-currency totals for the accounts detail view
        const totalSGD = accounts.reduce((sum, acc) => sum + (acc.balanceSGD || 0), 0);
        const totalUSD = accounts.reduce((sum, acc) => sum + (acc.balanceUSD || 0), 0);
        const totalLKR = accounts.reduce((sum, acc) => sum + (acc.balanceLKR || 0), 0);
        // Base-converted total for dashboards — convert each account's primary balance
        const totalBase = accounts.reduce((sum, acc) => {
            const cur = acc.currency || 'SGD';
            const primary = acc[`balance${cur}`] || 0;
            return sum + toBase(primary, cur);
        }, 0);
        return { totalSGD, totalUSD, totalLKR, totalBase };
    }, [accounts, toBase]);

    const filteredCreditCards = useMemo(() => {
        if (!searchQuery) return creditCards;
        const query = searchQuery.toLowerCase();
        return creditCards.filter(card =>
            card.cardName.toLowerCase().includes(query) ||
            card.cardNumber.toLowerCase().includes(query) ||
            card.bank.toLowerCase().includes(query) ||
            card.country.toLowerCase().includes(query)
        );
    }, [creditCards, searchQuery]);

    // Calculate credit card totals
    const cardTotals = useMemo(() => {
        const totalSGD = creditCards.reduce((sum, card) => sum + (card.balanceSGD || 0), 0);
        const totalUSD = creditCards.reduce((sum, card) => sum + (card.balanceUSD || 0), 0);
        const totalLKR = creditCards.reduce((sum, card) => sum + (card.balanceLKR || 0), 0);
        const totalBase = creditCards.reduce((sum, card) => {
            const cur = card.currency || 'SGD';
            const primary = card[`balance${cur}`] || 0;
            return sum + toBase(primary, cur);
        }, 0);
        return { totalSGD, totalUSD, totalLKR, totalBase };
    }, [creditCards, toBase]);

    const handleAdd = (item) => {
        if (modalType === 'gold-inventory') {
            setGoldInventory([...goldInventory, { ...item, id: Date.now() }]);
        } else if (modalType === 'personal-loan') {
            // Defensive defaults ensure currency & paymentHistory are always present
            // even if the form template somehow omits them (Bug Fix #2)
            setPersonalLoans([...personalLoans, { currency: 'SGD', paymentHistory: [], ...item, id: Date.now() }]);
        } else if (modalType === 'gold-loan') {
            setGoldLoans([...goldLoans, { currency: 'LKR', paymentHistory: [], ...item, id: Date.now() }]);
        } else if (modalType === 'account') {
            setAccounts([...accounts, { ...item, id: Date.now() }]);
        } else if (modalType === 'credit-card') {
            setCreditCards([...creditCards, { ...item, id: Date.now() }]);
        } else if (modalType === 'income') {
            setMonthlyIncomes([...monthlyIncomes, {
                currency: 'SGD', frequency: 'monthly', enabled: true,
                createdAt: new Date().toISOString(),
                ...item,
                id: `inc-${Date.now()}`,
            }]);
        }
        setShowAddModal(false);
    };

    const handleEdit = (updatedItem) => {
        if (modalType === 'gold-inventory') {
            setGoldInventory(goldInventory.map(item => item === editingItem ? updatedItem : item));
        } else if (modalType === 'personal-loan') {
            setPersonalLoans(personalLoans.map(loan => loan === editingItem ? updatedItem : loan));
        } else if (modalType === 'gold-loan') {
            setGoldLoans(goldLoans.map(loan => loan === editingItem ? updatedItem : loan));
        } else if (modalType === 'account') {
            setAccounts(accounts.map(acc => acc === editingItem ? updatedItem : acc));
        } else if (modalType === 'credit-card') {
            setCreditCards(creditCards.map(card => card === editingItem ? updatedItem : card));
        } else if (modalType === 'income') {
            setMonthlyIncomes(monthlyIncomes.map(inc => inc === editingItem ? { ...editingItem, ...updatedItem } : inc));
        }
        setShowEditModal(false);
        setEditingItem(null);
    };

    const handleDelete = (item) => {
        const itemName = item.name || item.accountName || item.cardName || 'this item';
        if (confirm(`Delete "${itemName}"?`)) {
            if (modalType === 'gold-inventory') {
                setGoldInventory(goldInventory.filter(i => i !== item));
            } else if (modalType === 'personal-loan') {
                setPersonalLoans(personalLoans.filter(l => l !== item));
            } else if (modalType === 'gold-loan') {
                setGoldLoans(goldLoans.filter(l => l !== item));
            } else if (modalType === 'account') {
                setAccounts(accounts.filter(a => a !== item));
            } else if (modalType === 'credit-card') {
                setCreditCards(creditCards.filter(c => c !== item));
            } else if (modalType === 'income') {
                setMonthlyIncomes(monthlyIncomes.filter(i => i !== item));
            }
        }
    };


    return (
        <>
            {/* Restoring session — silent token refresh in progress */}
            {authRestoring && (
                <div className="auth-restoring">
                    <div className="auth-restoring-logo">💎</div>
                    <div className="auth-restoring-name">iPlanner</div>
                    <div className="auth-restoring-dots">
                        <span></span><span></span><span></span>
                    </div>
                    <div className="auth-restoring-label">Restoring your session…</div>
                </div>
            )}

            {/* Auth Gate — show login screen only when fully checked and not signed in */}
            {authChecked && !authUser && !authRestoring && (
                <AuthScreen onSignedIn={handleSignedIn} />
            )}

            {authChecked && authUser && (
                <>
                    <div className={`offline-indicator ${!isOnline ? 'show' : ''}`}>
                        You are currently offline. Changes will be saved locally.
                    </div>

                    {showInstallPrompt && (
                        <div className="pwa-install-prompt">
                            <div>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Install App</div>
                                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Add to home screen for quick access</div>
                            </div>
                            <button className="pwa-install-btn" onClick={handleInstallClick}>
                                Install
                            </button>
                            <button
                                onClick={dismissInstallPrompt}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    fontSize: '20px',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>
                    )}
                    <TopBar
                        activeSection={activeSection}
                        activeModule={activeModule}
                        onModuleChange={(moduleId, targetSection) => {
                            // If the sub-tab belongs to a different section, switch section too
                            if (targetSection && targetSection !== activeSection) {
                                setActiveSection(targetSection);
                            }
                            setActiveModule(moduleId);
                        }}
                        goldCount={goldInventory.length}
                        loanCount={personalLoans.length + goldLoans.length}
                        syncStatus={syncStatus}
                        lastSynced={lastSynced}
                        onSyncNow={syncNow}
                        isOnline={isOnline}
                        baseCurrencyLabel={baseCurrencyLabel}
                        syncStuck={syncStuck}
                        onForceReconnect={forceReconnect}
                    />

                    {/* ── Cycle Confirm Modal ───────────────────────────────── */}
                    <CycleConfirmModal
                        showCycleConfirm={showCycleConfirm}
                        setShowCycleConfirm={setShowCycleConfirm}
                        cycleResetStatus={cycleResetStatus}
                        handleCloseCycle={handleCloseCycle}
                        cycleOpenedAt={cycleOpenedAt}
                        currentCycle={currentCycle}
                    />

                    {activeSection === 'dashboard' && (
                        <DashboardSection
                            baseCurrencyLabel={baseCurrencyLabel}
                            fmtBase={fmtBase}
                            goldTotals={goldTotals}
                            accountTotals={accountTotals}
                            investmentTotals={investmentTotals}
                            fixedAssetTotals={fixedAssetTotals}
                            loanTotals={loanTotals}
                            cardTotals={cardTotals}
                            goldInventory={goldInventory}
                            accounts={accounts}
                            investments={investments}
                            fixedAssets={fixedAssets}
                            personalLoans={personalLoans}
                            goldLoans={goldLoans}
                            creditCards={creditCards}
                            handleOpenScorecard={handleOpenScorecard}
                            setActiveSection={setActiveSection}
                            setActiveModule={setActiveModule}
                        />
                    )}

                    {activeSection === 'assets' && (
                        <AssetsSection
                            goldInventory={goldInventory}
                            filteredGoldInventory={filteredGoldInventory}
                            sgdRate={sgdRate}
                            accounts={accounts}
                            filteredAccounts={filteredAccounts}
                            accountTotals={accountTotals}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            activeModule={activeModule}
                            setModalType={setModalType}
                            setShowAddModal={setShowAddModal}
                            setEditingItem={setEditingItem}
                            setShowEditModal={setShowEditModal}
                            baseCurrencyLabel={baseCurrencyLabel}
                            fmtBase={fmtBase}
                            toBase={toBase}
                        />
                    )}

                    {activeSection === 'liabilities' && (
                        <LiabilitiesSection
                            personalLoans={personalLoans}
                            setPersonalLoans={setPersonalLoans}
                            filteredPersonalLoans={filteredPersonalLoans}
                            goldLoans={goldLoans}
                            setGoldLoans={setGoldLoans}
                            filteredGoldLoans={filteredGoldLoans}
                            creditCards={creditCards}
                            setCreditCards={setCreditCards}
                            filteredCreditCards={filteredCreditCards}
                            cardTotals={cardTotals}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            activeModule={activeModule}
                            setModalType={setModalType}
                            setShowAddModal={setShowAddModal}
                            setEditingItem={setEditingItem}
                            setShowEditModal={setShowEditModal}
                            handleMarkLoanPaid={handleMarkLoanPaid}
                            handleMarkGoldLoanPaid={handleMarkGoldLoanPaid}
                            expandedLoanId={expandedLoanId}
                            setExpandedLoanId={setExpandedLoanId}
                            baseCurrencyLabel={baseCurrencyLabel}
                            fmtBase={fmtBase}
                            toBase={toBase}
                            sgdRate={sgdRate}
                            lkrRate={lkrRate}
                        />
                    )}

                    {activeSection === 'budget' && (
                        <BudgetSection
                            sgBudgetCycle1={sgBudgetCycle1}
                            setSgBudgetCycle1={setSgBudgetCycle1}
                            sgBudgetCycle2={sgBudgetCycle2}
                            setSgBudgetCycle2={setSgBudgetCycle2}
                            lkBudgetDamith={lkBudgetDamith}
                            setLkBudgetDamith={setLkBudgetDamith}
                            lkBudgetShan={lkBudgetShan}
                            setLkBudgetShan={setLkBudgetShan}
                            currentCycle={currentCycle}
                            cycleOpenedAt={cycleOpenedAt}
                            isNewCycle={isNewCycle}
                            cycleResetStatus={cycleResetStatus}
                            setShowCycleConfirm={setShowCycleConfirm}
                            showCycleConfirm={showCycleConfirm}
                            cycleHistory={cycleHistory}
                            expandedHistoryId={expandedHistoryId}
                            setExpandedHistoryId={setExpandedHistoryId}
                            handleAddSpend={handleAddSpend}
                            activeModule={activeModule}
                            setActiveModule={setActiveModule}
                            baseCurrencyLabel={baseCurrencyLabel}
                            fmtBase={fmtBase}
                            toBase={toBase}
                            sgdRate={sgdRate}
                            lkrRate={lkrRate}
                            monthlyIncomes={monthlyIncomes}
                            setMonthlyIncomes={setMonthlyIncomes}
                            toMonthlyBase={toMonthlyBase}
                            incomeTotals={incomeTotals}
                            budgetedMonthlySpend={budgetedMonthlySpend}
                            setModalType={setModalType}
                            setShowAddModal={setShowAddModal}
                            setEditingItem={setEditingItem}
                            setShowEditModal={setShowEditModal}
                            handleDelete={handleDelete}
                        />
                    )}

                    {activeSection === 'settings' && (
                        <SettingsSection
                            authUser={authUser}
                            lastSynced={lastSynced}
                            syncStatus={syncStatus}
                            isOnline={isOnline}
                            syncNow={syncNow}
                            handleSignOut={handleSignOut}
                            baseCurrency={baseCurrency}
                            setBaseCurrency={setBaseCurrency}
                            sgdRate={sgdRate}
                            lkrRate={lkrRate}
                            usdRate={usdRate}
                            setSgdRate={setSgdRate}
                            setLkrRate={setLkrRate}
                            setUsdRate={setUsdRate}
                            driveBackups={driveBackups}
                            backupStatus={backupStatus}
                            createBackup={createBackup}
                            restoreBackup={restoreBackup}
                            fiReportStatus={fiReportStatus}
                            shareWithFI={shareWithFI}
                            exportAdvisorReportHTML={exportAdvisorReportHTML}
                            exportAdvisorReportPDF={exportAdvisorReportPDF}
                            exportHouseholdReportHTML={exportHouseholdReportHTML}
                            exportHouseholdReportPDF={exportHouseholdReportPDF}
                            goldTotals={goldTotals}
                            accountTotals={accountTotals}
                            cardTotals={cardTotals}
                            loanTotals={loanTotals}
                            investmentTotals={investmentTotals}
                            fixedAssetTotals={fixedAssetTotals}
                            monthlyIncomes={monthlyIncomes}
                            fmtBase={fmtBase}
                            loadDriveBackups={loadDriveBackups}
                            shareAdvisorReport={shareAdvisorReport}
                            advisorReportStatus={advisorReportStatus}
                            driveBackupsLoading={driveBackupsLoading}
                            initialGoldInventory={initialGoldInventory}
                            initialPersonalLoans={initialPersonalLoans}
                            initialGoldLoans={initialGoldLoans}
                            initialAccounts={initialAccounts}
                            setGoldInventory={setGoldInventory}
                            setPersonalLoans={setPersonalLoans}
                            setGoldLoans={setGoldLoans}
                            setAccounts={setAccounts}
                            setCreditCards={setCreditCards}
                            setSgBudgetCycle1={setSgBudgetCycle1}
                            setSgBudgetCycle2={setSgBudgetCycle2}
                            setLkBudgetDamith={setLkBudgetDamith}
                            setLkBudgetShan={setLkBudgetShan}
                            goldInventory={goldInventory}
                            personalLoans={personalLoans}
                            goldLoans={goldLoans}
                            accounts={accounts}
                            creditCards={creditCards}
                            sgBudgetCycle1={sgBudgetCycle1}
                            sgBudgetCycle2={sgBudgetCycle2}
                            lkBudgetDamith={lkBudgetDamith}
                            lkBudgetShan={lkBudgetShan}
                            baseCurrencyLabel={baseCurrencyLabel}
                            DRIVE_FOLDER_PATH={DRIVE_FOLDER_PATH}
                            DRIVE_FILE_NAME={DRIVE_FILE_NAME}
                        />
                    )}

                    {/* ── NG-3: Monthly Performance Scorecard Modal ─────────── */}
                    <ScorecardModal
                        showScorecard={showScorecard}
                        setShowScorecard={setShowScorecard}
                        scorecardData={scorecardData}
                        baseCurrencyLabel={baseCurrencyLabel}
                        fmtBase={fmtBase}
                    />

                    {/* ── Investments Section ──────────────────────────────── */}
                    {activeSection === 'investments' && (
                        <InvestmentsSection
                            activeModule={activeModule}
                            investments={investments}
                            setInvestments={setInvestments}
                            investmentTotals={investmentTotals}
                            fmtBase={fmtBase}
                            toBase={toBase}
                            invCashSGD={invCashSGD}
                            setInvCashSGD={setInvCashSGD}
                            invCashUSD={invCashUSD}
                            setInvCashUSD={setInvCashUSD}
                            moneyMarketFunds={moneyMarketFunds}
                            setMoneyMarketFunds={setMoneyMarketFunds}
                        />
                    )}

                    {/* ── Fixed Assets Section ─────────────────────────────── */}
                    {activeSection === 'fixed-assets' && (
                        <FixedAssetsSection
                            fixedAssets={fixedAssets}
                            setFixedAssets={setFixedAssets}
                            toBase={toBase}
                            fmtBase={fmtBase}
                            baseCurrencyLabel={baseCurrencyLabel}
                            fixedAssetTotals={fixedAssetTotals}
                            lkrRate={lkrRate}
                        />
                    )}

                    {/* ── Milestone Journey Section (RPT-03) ───────────────── */}
                    {activeSection === 'milestones' && (
                        <MilestonesSection
                            baseCurrency={baseCurrency}
                            financialMilestones={financialMilestones}
                            setFinancialMilestones={setFinancialMilestones}
                            msForm={msForm}
                            setMsForm={setMsForm}
                            msEditId={msEditId}
                            setMsEditId={setMsEditId}
                            showMsModal={showMsModal}
                            setShowMsModal={setShowMsModal}
                            getAllData={getAllData}
                        />
                    )}

                    {/* ── FI Advisor Section ──────────────────────────────── */}
                    {activeSection === 'fi-advisor' && (
                        <FIAdvisorSection generateFIReport={generateFIReport} />
                    )}
                    {/* ── END FI Advisor Section ────────────────────────────── */}


                    <button
                        className={`fab-log-spend ${fabVisible ? '' : 'fab-hidden'}`}
                        onClick={() => {
                            setFabBudget(null);
                            setFabCategory(null);
                            setFabAmt('');
                            setFabNote('');
                            setShowFAB(true);
                        }}
                        title="Log Spend"
                        aria-label="Log Spend"
                    >
                        ＋
                    </button>

                    {/* NG-1: FAB Quick-Log Sheet */}
                    <FabSpendSheet
                        showFAB={showFAB}
                        setShowFAB={setShowFAB}
                        fabBudget={fabBudget}
                        setFabBudget={setFabBudget}
                        fabCategory={fabCategory}
                        setFabCategory={setFabCategory}
                        fabAmt={fabAmt}
                        setFabAmt={setFabAmt}
                        fabNote={fabNote}
                        setFabNote={setFabNote}
                        handleFABSubmit={handleFABSubmit}
                        fabBudgetConfig={fabBudgetConfig}
                    />

                    {/* ── More Drawer ───────────────────────────────────────── */}
                    <MoreDrawer
                        showMoreDrawer={showMoreDrawer}
                        setShowMoreDrawer={setShowMoreDrawer}
                        setActiveSection={setActiveSection}
                        setActiveModule={setActiveModule}
                    />

                    {/* ── Bottom Nav — 5 grouped tabs ───────────────────────── */}
                    <BottomNav
                        activeSection={activeSection}
                        setActiveSection={setActiveSection}
                        setActiveModule={setActiveModule}
                        showMoreDrawer={showMoreDrawer}
                        setShowMoreDrawer={setShowMoreDrawer}
                        personalLoans={personalLoans}
                    />

                    {showAddModal && (
                        <FormModal
                            onClose={() => setShowAddModal(false)}
                            onSave={handleAdd}
                            modalType={modalType}
                            sgdRate={sgdRate}
                            lkrRate={lkrRate}
                            usdRate={usdRate}
                            title={`Add ${modalType === 'gold-inventory' ? 'Gold Item' : modalType === 'personal-loan' ? 'Personal Loan' : modalType === 'gold-loan' ? 'Gold Loan' : 'Account'}`}
                        />
                    )}

                    {showEditModal && editingItem && (
                        <FormModal
                            onClose={() => {
                                setShowEditModal(false);
                                setEditingItem(null);
                            }}
                            onSave={handleEdit}
                            onDelete={() => handleDelete(editingItem)}
                            initialItem={editingItem}
                            modalType={modalType}
                            sgdRate={sgdRate}
                            lkrRate={lkrRate}
                            usdRate={usdRate}
                            title={`Edit ${modalType === 'gold-inventory' ? 'Gold Item' : modalType === 'personal-loan' ? 'Personal Loan' : modalType === 'gold-loan' ? 'Gold Loan' : 'Account'}`}
                        />
                    )}
                </>
            )}
        </>
    );
}

export default App;